import { useCallback, useMemo } from 'react';
import {
  type FieldArrayPath,
  type FieldValues,
  get,
  type UseFieldArrayProps,
  type UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';
import { useTriggerComputedSync } from '../../hooks/useTriggerComputedSync.ts';
import { setByPath } from '../../utils/pathAccessors.ts';

// Import originali da RHF che andiamo a sovrascrivere

// biome-ignore lint/style/noRestrictedImports: è necessario importare direttamente da react-hook-form per sovrascriverlo
import { useFieldArray } from 'react-hook-form';

/**
 * Wrapper di `useFieldArray` (react-hook-form) con metodi mutanti arricchiti
 * che schedulano automaticamente `syncComputedValues`.
 *
 * Utilizzare al posto di `useFieldArray` da `react-hook-form` in tutti
 * i componenti.
 */
export const useFieldArrayOf = <
  T,
  TFieldValues extends FieldValues = { [key: string]: T[] },
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>(
  props: UseFieldArrayProps<TFieldValues, TFieldArrayName, TKeyName>,
): UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName> => {
  const result = useFieldArray(props);
  const triggerSync = useTriggerComputedSync();
  const { control } = useFormContext();

  // Dopo ogni mutazione dell'array, allinea _defaultValues con _formValues
  // per il path dell'array. Senza questo, register() di RHF legge default
  // stale da _defaultValues (che non viene reindiciato da _setFieldArray)
  // e li scrive in _formValues, corrompendo i valori dopo remove/append.
  const syncDefaults = useCallback(() => {
    const current = get((control as any)._formValues, props.name);
    if (Array.isArray(current)) {
      setByPath(
        (control as any)._defaultValues,
        props.name as string,
        current.map((item: any) => (item != null ? { ...item } : item)),
      );
    }
  }, [control, props.name]);

  const append: typeof result.append = useCallback(
    (...args) => {
      result.append(...args);
      syncDefaults();
      triggerSync();
    },
    [result.append, syncDefaults, triggerSync],
  );

  const prepend: typeof result.prepend = useCallback(
    (...args) => {
      result.prepend(...args);
      syncDefaults();
      triggerSync();
    },
    [result.prepend, syncDefaults, triggerSync],
  );

  const insert: typeof result.insert = useCallback(
    (...args) => {
      result.insert(...args);
      syncDefaults();
      triggerSync();
    },
    [result.insert, syncDefaults, triggerSync],
  );

  const remove: typeof result.remove = useCallback(
    (...args) => {
      result.remove(...args);
      syncDefaults();
      triggerSync();
    },
    [result.remove, syncDefaults, triggerSync],
  );

  const update: typeof result.update = useCallback(
    (...args) => {
      result.update(...args);
      syncDefaults();
      triggerSync();
    },
    [result.update, syncDefaults, triggerSync],
  );

  const swap: typeof result.swap = useCallback(
    (...args) => {
      result.swap(...args);
      syncDefaults();
      triggerSync();
    },
    [result.swap, syncDefaults, triggerSync],
  );

  const move: typeof result.move = useCallback(
    (...args) => {
      result.move(...args);
      syncDefaults();
      triggerSync();
    },
    [result.move, syncDefaults, triggerSync],
  );

  const replace: typeof result.replace = useCallback(
    (...args) => {
      result.replace(...args);
      syncDefaults();
      triggerSync();
    },
    [result.replace, syncDefaults, triggerSync],
  );

  return useMemo(
    () => ({
      ...result,
      append,
      prepend,
      insert,
      remove,
      update,
      swap,
      move,
      replace,
    }),
    [result, append, prepend, insert, remove, update, swap, move, replace],
  );
};
